import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { Container, Modal, Button, Form } from "react-bootstrap";
import "react-data-grid/lib/styles.css";
import { useEffect, useState, useCallback, useRef } from "react";
import { RootState } from "../redux/store";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../hooks/app";
import {
  fetchAllServiceCenters,
  createServiceCenter,
  getAllServiceCenters,
} from "../redux/actions/globalServiceCenterActions";
import GridTable from "../components/Table";
import moment from "moment-timezone";
import { getAllOrgnizationForDropdown } from "../redux/actions/orgnization";
import HSSelect from "../components/Select";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export const GlobalServiceCenterList = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const dispatch = useAppDispatch();
  const { serviceCenters, loading } = useSelector(
    (state: RootState) => state.globalServiceCenter
  );
  const org = useSelector((state: RootState) => state.organization);

  const [rows, setRows] = useState<any[]>([]);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [searchString, setSearchString] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [centerName, setCenterName] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgOptions, setOrgOptions] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [total, setTotal] = useState(0);
  const [orgPageNo, setOrgPageNo] = useState(1);
  const [orgSearchString, setOrgSearchString] = useState("");
  const [isPaginationVisible, setIsPaginationVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(orgSearchString, 500);

  // Use a more reliable approach - check if we're at the bottom of the container
  const handleContainerScroll = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if we're within 100px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setIsPaginationVisible(isAtBottom);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleContainerScroll);
      // Check initial state
      handleContainerScroll();

      return () => {
        container.removeEventListener("scroll", handleContainerScroll);
      };
    }
  }, [handleContainerScroll, rows]); // Re-run when rows change

  useEffect(() => {
    const queryData: any = {
      page,
      limit,
      searchParam: searchString,
    };
    dispatch(getAllServiceCenters(queryData));
  }, [page, limit, searchString]);

  useEffect(() => {
    const data: any = {
      page: orgPageNo,
      searchParam: debouncedSearch || "",
      serviceCenter: true,
    };
    dispatch(getAllOrgnizationForDropdown(data));
  }, [orgPageNo, debouncedSearch, dispatch]);

  useEffect(() => {
    if (serviceCenters[0] && Array.isArray(serviceCenters[0])) {
      setRows(
        serviceCenters[0].map((sc: any) => ({
          id: sc.id,
          name: sc.name,
          organization: sc.orgnization?.name || "", // <-- show organization name
          email: sc.emailId,
          contact: sc.contact,
        }))
      );
    } else {
      setRows([]);
    }
  }, [serviceCenters]);

  useEffect(() => {
    if (org?.orgList?.data?.length > 0) {
      const filteredOrgs = org.orgList?.data?.map((ele: any) => ({
        id: ele.id,
        name: ele.name,
      }));

      if (orgPageNo === 1) {
        setOrgOptions(filteredOrgs);
      } else {
        setOrgOptions((prev: any) => [...prev, ...filteredOrgs]);
      }
    }
    setTotal(org?.orgList?.total || 0);
  }, [org?.orgList, orgPageNo]);

  const handleSearch = (value: string, action?: any) => {
    if (action?.action === "input-change") {
      setOrgSearchString(value);
      setOrgPageNo(1);
    }
  };

  const resetForm = () => {
    setCenterName("");
    setContactPersonName("");
    setContact("");
    setEmail("");
    setOrgId("");
    setErrors({});
  };

  const handleCancel = () => {
    setShowModal(false);
    resetForm();
  };

  const handleAddServiceCenter = () => {
    if (!validate()) return;
    dispatch(
      createServiceCenter({
        name: centerName,
        contact_person_name: contactPersonName,
        contact: contact,
        emailId: email,
        orgId: orgId,
        isActive: true,
        isDeleted: false,
      })
    ).then(() => {
      setShowModal(false);
      resetForm();
      const queryData: any = {
        page,
        limit,
        searchParam: searchString,
      };
      dispatch(getAllServiceCenters(queryData));
    });
  };

  const validate = () => {
    const newErrors: any = {};
    if (!orgId) newErrors.orgId = "Please select organization";
    if (!centerName) newErrors.centerName = "Please enter service center name";
    if (!contactPersonName)
      newErrors.contactPersonName = "Please enter contact person name";
    if (!contact) {
      newErrors.contact = "Please enter contact number";
    } else if (contact.length < 10) {
      newErrors.contact = "Contact number must be at least 10 digits";
    }
    if (!email) newErrors.email = "Please enter email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Please enter a valid email";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const columns: any = [
    {
      key: "name",
      name: "Service Center Name",
      sortable: true,
      draggable: true,
    },
    {
      key: "organization",
      name: "Organization Name", // <-- update column name
      sortable: true,
      draggable: true,
    },
    {
      key: "email",
      name: "Email",
      sortable: true,
      draggable: true,
    },
    {
      key: "contact",
      name: "Contact",
      sortable: true,
      draggable: true,
    },
  ];

  return (
    <AuthenticatedTemplate>
      {activeAccount ? (
        <Container
          ref={containerRef}
          className="h-full bg-white p-4 min-w-[75vw] font-['poppins'] overflow-auto"
        >
          <div className="xl:h-[57vh] 2xl:h-[65vh] 1xl:h-[58vh] lg:h-[55vh] md:h-[55vh] ">
            <GridTable
              rowData={rows}
              columnData={columns}
              showAction={false}
              onPageChange={(pageNumber) => setPage(pageNumber)}
              onRowsPerPageChange={(limit) => setLimit(limit)}
              totalCount={serviceCenters[1]}
              onClickSearch={(searchKey) => {
                setPage(1);
                setSearchString(searchKey);
              }}
              // loading={loading}
            />
          </div>

          <Modal show={showModal} onHide={handleCancel} centered>
            <Modal.Header closeButton>
              <Modal.Title>Add Service Center</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group>
                  <HSSelect
                    placeholder="Select Organization"
                    options={orgOptions}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option.id}
                    value={orgOptions.find((opt) => opt.id === orgId) || null}
                    onChange={(obj: any) => {
                      setOrgId(obj ? obj.id : "");
                      setErrors({ ...errors, orgId: "" });
                    }}
                    error={errors.orgId}
                    // required={true}
                    onMenuScrollToBottom={() => {
                      if (orgOptions.length < total) {
                        setOrgPageNo((prev) => prev + 1);
                      }
                    }}
                    onInputChange={(value: string, action: any) =>
                      handleSearch(value, action)
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Service Center Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={centerName}
                    onChange={(e) => {
                      setCenterName(e.target.value);
                      setErrors({ ...errors, centerName: "" });
                    }}
                    placeholder="Enter service center name"
                    isInvalid={!!errors.centerName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.centerName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mt-3">
                  <Form.Label>Contact Person Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={contactPersonName}
                    onChange={(e) => {
                      setContactPersonName(e.target.value);
                      setErrors({ ...errors, contactPersonName: "" });
                    }}
                    placeholder="Enter contact person name"
                    isInvalid={!!errors.contactPersonName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactPersonName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mt-3">
                  <Form.Label>Contact</Form.Label>
                  <Form.Control
                    type="text"
                    value={contact}
                    onChange={(e) => {
                      const re = /^[0-9\b]+$/;
                      if (
                        (re.test(e.target.value) &&
                          e.target.value.length < 11) ||
                        !e.target.value
                      ) {
                        setContact(e.target.value);
                        setErrors({ ...errors, contact: "" });
                      }
                    }}
                    placeholder="Enter contact number"
                    isInvalid={!!errors.contact}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contact}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mt-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: "" });
                    }}
                    placeholder="Enter email"
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAddServiceCenter}>
                Add
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Floating Plus Button */}
          <Button
            variant="primary"
            style={{
              position: "fixed",
              bottom: isPaginationVisible ? 120 : 32, // Move up when pagination is visible
              right: 32,
              borderRadius: "50%",
              width: 56,
              height: 56,
              fontSize: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              transition: "bottom 0.3s ease-in-out", // Smooth transition
            }}
            onClick={() => setShowModal(true)}
          >
            +
          </Button>
        </Container>
      ) : null}
    </AuthenticatedTemplate>
  );
};
